import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import { Layout } from './components/Layout';

// Pages
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import EmployeesPage from './pages/EmployeesPage';
import EmployeeFormPage from './pages/EmployeeFormPage';
import PayrollPage from './pages/PayrollPage';
import PayrollFormPage from './pages/PayrollFormPage';
import PayrollDetailPage from './pages/PayrollDetailPage';
import AdvancesPage from './pages/AdvancesPage';
import LoansPage from './pages/LoansPage';
import RecurringPage from './pages/RecurringPage';
import ExportPage from './pages/ExportPage';
import ArchivePage from './pages/ArchivePage';
import UsersPage from './pages/UsersPage';
import AuditLogPage from './pages/AuditLogPage';

// Protected Route Component
const ProtectedRoute = ({ children, requiredRoles = [] }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-[#E31E24] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (requiredRoles.length > 0 && !requiredRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return <Layout>{children}</Layout>;
};

// App Routes
const AppRoutes = () => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-[#E31E24] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  return (
    <Routes>
      {/* Public Routes */}
      <Route 
        path="/login" 
        element={user ? <Navigate to="/dashboard" replace /> : <LoginPage />} 
      />
      
      {/* Protected Routes */}
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/employees" 
        element={
          <ProtectedRoute>
            <EmployeesPage />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/employees/new" 
        element={
          <ProtectedRoute requiredRoles={['admin', 'hr']}>
            <EmployeeFormPage />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/employees/:id" 
        element={
          <ProtectedRoute>
            <EmployeeFormPage />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/employees/:id/edit" 
        element={
          <ProtectedRoute requiredRoles={['admin', 'hr']}>
            <EmployeeFormPage />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/payroll" 
        element={
          <ProtectedRoute>
            <PayrollPage />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/payroll/new" 
        element={
          <ProtectedRoute requiredRoles={['admin', 'hr']}>
            <PayrollFormPage />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/payroll/:id" 
        element={
          <ProtectedRoute>
            <PayrollDetailPage />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/payroll/:id/edit" 
        element={
          <ProtectedRoute requiredRoles={['admin', 'hr']}>
            <PayrollFormPage />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/advances" 
        element={
          <ProtectedRoute>
            <AdvancesPage />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/loans" 
        element={
          <ProtectedRoute>
            <LoansPage />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/recurring" 
        element={
          <ProtectedRoute>
            <RecurringPage />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/export" 
        element={
          <ProtectedRoute requiredRoles={['admin', 'hr']}>
            <ExportPage />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/archive" 
        element={
          <ProtectedRoute>
            <ArchivePage />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/users" 
        element={
          <ProtectedRoute requiredRoles={['admin']}>
            <UsersPage />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/audit-log" 
        element={
          <ProtectedRoute requiredRoles={['admin']}>
            <AuditLogPage />
          </ProtectedRoute>
        } 
      />
      
      {/* Redirect root to dashboard or login */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      
      {/* Catch all - redirect to dashboard */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <AppRoutes />
          <Toaster 
            position="top-right"
            toastOptions={{
              style: {
                background: '#121212',
                border: '1px solid #27272A',
                color: '#FFFFFF',
              },
              className: 'font-sans',
            }}
          />
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
